// SPDX-FileCopyrightText: Copyright (C) 2026 provide.io llc
// SPDX-License-Identifier: Apache-2.0

export interface ApiClientOptions {
  readonly baseUrl: string;
  readonly getToken: () => string | null;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly getToken: () => string | null;

  constructor(opts: ApiClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, "");
    this.getToken = opts.getToken;
  }

  private headers(extra?: HeadersInit): Headers {
    const h = new Headers(extra);
    h.set("accept", "application/json");
    const tok = this.getToken();
    if (tok) h.set("authorization", `Bearer ${tok}`);
    return h;
  }

  async get<T>(path: string, init?: RequestInit): Promise<T> {
    return this.request<T>(path, { ...init, method: "GET" });
  }

  async post<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    const next: RequestInit = {
      ...init,
      method: "POST",
      headers: { ...(init?.headers ?? {}), "content-type": "application/json" },
    };
    if (body != null) next.body = JSON.stringify(body);
    return this.request<T>(path, next);
  }

  private async request<T>(path: string, init: RequestInit): Promise<T> {
    const r = await fetch(this.baseUrl + path, {
      ...init,
      headers: this.headers(init.headers),
    });
    if (!r.ok) {
      let body: unknown;
      try {
        body = await r.json();
      } catch {
        body = await r.text().catch(() => "");
      }
      throw new ApiError(r.status, `${init.method} ${path} → ${r.status}`, body);
    }
    const ct = r.headers.get("content-type") ?? "";
    if (ct.startsWith("application/json")) return (await r.json()) as T;
    return (await r.text()) as unknown as T;
  }
}
